import React, { createContext, useContext, useState, useEffect, PropsWithChildren, useCallback, useRef } from 'react';
import { projectApi } from '../authentication/clients';
import { GetProjectResponse } from '../api';
import { useUser } from './UserContext';

interface ProjectsContextType {
    data: GetProjectResponse | null;
    loading: boolean;
    error: string | null;
    refresh: () => Promise<void>;
}

const ProjectsContext = createContext<ProjectsContextType | undefined>(undefined);

export const ProjectsProvider: React.FC<PropsWithChildren> = ({ children }) => {
    const user = useUser();
    const [data, setData] = useState<GetProjectResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const hasFetchedRef = useRef(false);
    const isFetchingRef = useRef(false);

    const fetchFamilies = useCallback(async (force = false) => {
        // Only fetch if user is signed in
        if (!user.email) {
            setLoading(false);
            hasFetchedRef.current = true;
            return;
        }

        // If we already have data and this is not a forced refresh, skip the API call
        if (hasFetchedRef.current && !force) {
            setLoading(false);
            return;
        }

        // Prevent multiple concurrent fetches
        if (isFetchingRef.current) {
            return;
        }

        try {
            isFetchingRef.current = true;
            setLoading(true);
            setError(null);
            const response = await projectApi.getProjects();
            setData(response.data);
            hasFetchedRef.current = true;
        } catch (err: any) {
            setError(err?.response?.data?.message || 'Failed to load families');
            setData(null);
            hasFetchedRef.current = true;
        } finally {
            setLoading(false);
            isFetchingRef.current = false;
        }
    }, [user.email]);

    const refresh = useCallback(async () => {
        await fetchFamilies(true);
    }, [fetchFamilies]);

    useEffect(() => {
        fetchFamilies();
    }, [fetchFamilies]);

    // Reset cache when user changes
    useEffect(() => {
        hasFetchedRef.current = false;
        isFetchingRef.current = false;
        setData(null);
        setError(null);
    }, [user.email]);

    const contextValue: ProjectsContextType = {
        data,
        loading,
        error,
        refresh,
    };

    return (
        <ProjectsContext.Provider value={contextValue}>
            {children}
        </ProjectsContext.Provider>
    );
};

export const useProjects = (): ProjectsContextType => {
    const context = useContext(ProjectsContext);
    if (!context) {
        throw new Error('useProjects must be used within a ProjectsProvider');
    }
    return context;
};
